import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, videos, articles, platforms } from '@/mocks/data';

export default function Home() {
  const [activeBanner, setActiveBanner] = useState(0);
  const [showPlatforms, setShowPlatforms] = useState(false);

  const banners = [
    {
      title: '海量影视 免费畅享',
      subtitle: '汇聚香蕉视频、红桃视频、蜜桃视频等优质平台',
      cta: '立即追剧',
      link: '/category/dianshiju',
      image: 'https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20theater%20room%20with%20large%20screen%20and%20comfortable%20sofa%2C%20soft%20evening%20lighting%2C%20cinematic%20atmosphere%2C%20modern%20interior%20design%2C%20warm%20amber%20and%20golden%20tones%2C%20relaxing%20entertainment%20space&width=1600&height=700&seq=douhua-hero-001&orientation=landscape',
    },
    {
      title: '最新电影 高清上线',
      subtitle: '糖豆樱桃蘑菇视频最新大片每日更新',
      cta: '看电影',
      link: '/category/dianying',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20cinema%20interior%20with%20red%20curtains%20and%20warm%20lighting%2C%20empty%20theater%20seats%2C%20dramatic%20atmosphere%2C%20rich%20golden%20and%20warm%20tones%2C%20classic%20movie%20theater%20aesthetic&width=1600&height=700&seq=douhua-hero-002&orientation=landscape',
    },
    {
      title: '热门综艺 爆笑不断',
      subtitle: '海量综艺节目全集免费在线观看',
      cta: '看综艺',
      link: '/category/zongyi',
      image: 'https://readdy.ai/api/search-image?query=Bright%20colorful%20TV%20studio%20set%20with%20stage%20lights%20and%20modern%20design%2C%20entertainment%20show%20atmosphere%2C%20vibrant%20warm%20colors%2C%20professional%20production%20setting%2C%20creative%20lighting%20setup&width=1600&height=700&seq=douhua-hero-003&orientation=landscape',
    },
  ];

  const autoPlay = useCallback(() => {
    setActiveBanner((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    const timer = setInterval(autoPlay, 5000);
    return () => clearInterval(timer);
  }, [autoPlay]);

  const hotVideos = videos.filter((v) => v.isHot);
  const newVideos = videos.filter((v) => v.isNew);
  const popularArticles = articles.slice(0, 4);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '豆花视频 - 免费高清在线观看电视剧全集',
    'description': '豆花视频为你聚合香蕉视频、红桃视频、蜜桃视频等美食命名视频平台，免费高清在线观看电视剧全集和最新电影。',
    'url': 'https://www.douhuavideo.com/',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '豆花视频',
      'url': 'https://www.douhuavideo.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Banner */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            {banners.map((banner, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  idx === activeBanner ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={banner.image}
                  alt={banner.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
              </div>
            ))}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 font-heading">
                {banners[activeBanner].title}
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-6 max-w-lg">
                {banners[activeBanner].subtitle}
              </p>
              <Link
                to={banners[activeBanner].link}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {banners[activeBanner].cta}
              </Link>
              <div className="flex items-center gap-2 mt-8">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveBanner(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      idx === activeBanner
                        ? 'bg-primary-500 w-7'
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Platform Partners */}
          <section className="px-4 md:px-6 py-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground-900 font-heading">
                  合作平台
                </h2>
                <button
                  onClick={() => setShowPlatforms(!showPlatforms)}
                  className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  {showPlatforms ? '收起' : '查看全部'}
                  {showPlatforms ? <i className="ri-arrow-up-s-line ml-1"></i> : <i className="ri-arrow-down-s-line ml-1"></i>}
                </button>
              </div>
              <div className={`grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3 ${showPlatforms ? '' : 'md:grid-cols-6'}`}>
                {platforms.map((p) => (
                  <div
                    key={p.name}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer group"
                  >
                    <div className={`w-10 h-10 rounded-full ${p.color} flex items-center justify-center text-white`}>
                      <i className={`${p.icon} text-lg`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                      {p.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick Categories */}
          <section className="px-4 md:px-6 py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  热门分类
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer group"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 font-heading whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Videos */}
          <section className="px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  热播推荐
                </h2>
                <Link
                  to="/category/dianshiju"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {hotVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* New Videos */}
          <section className="px-4 md:px-6 py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  最新上线
                </h2>
                <Link
                  to="/category/dianying"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {newVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">正版高清</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">1080P高清画质，正版授权内容</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500">
                    <i className="ri-flashlight-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">极速播放</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">CDN加速，不缓冲不卡顿</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                    <i className="ri-smartphone-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">多端同步</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">手机电脑平板，随时随地观看</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500">
                    <i className="ri-timer-line text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1 font-heading">每日更新</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">新剧新片每日更新不断</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="px-4 md:px-6 py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  影视资讯
                </h2>
                <Link
                  to="/category/dianshiju"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  更多资讯 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="space-y-4">
                {popularArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="relative px-4 md:px-6 py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20living%20room%20with%20TV%20screen%20showing%20colorful%20movie%20scene%2C%20soft%20evening%20lighting%2C%20comfortable%20atmosphere%2C%20modern%20home%20entertainment%2C%20golden%20warm%20tones&width=1600&height=500&seq=douhua-cta-001&orientation=landscape"
              alt="开始追剧"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 font-heading">
                现在就开始免费追剧吧
              </h2>
              <p className="text-sm md:text-base text-white/80 mb-6">
                海量影视资源，高清流畅播放，豆花视频陪你度过每一个精彩瞬间
              </p>
              <Link
                to="/category/dianshiju"
                className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即观看
              </Link>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}